import React, { Component } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import classNames from "classnames";

// UI Components
import BootstrapTable from "react-bootstrap-table-next"; // https://react-bootstrap-table.github.io/react-bootstrap-table2/storybook/index.html
import paginationFactory from "react-bootstrap-table2-paginator";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell, faUserPlus } from "@fortawesome/free-solid-svg-icons";
import { toastr } from "react-redux-toastr";
import { Row, Col, Button, Container, Card, CardBody, Alert } from "reactstrap";

// State
import {
  getWhitelist,
  selectWhitelist,
  createWhitelist,
  updateWhitelist,
  deleteWhitelist,
  escapeRegExp,
} from "../../../redux/actions/adminActions";

// Custom Components
import { Group } from "../../../utils/logger";
import Loader from "../../../components/Loader";
import Header from "./Header";
import whitelist_table_schema from "./whitelistTableSchema";
import WhitelistModal from "./Modal.WhitelistRecord";

// Globals
const RECORDS_PER_PAGE = "mbEonsRecordsPerPage";

class Whitelist extends Component {
  constructor(props) {
    super(props);

    this.state = {
      search_text: "",
      recordsPerPage: localStorage.getItem(RECORDS_PER_PAGE) || 50,

      // Modal
      modal: {
        type: "add",
        isOpen: false,
        applyChanges: null,
      },
    };

    this.LoggerGroup = new Group("Whitelist", { disable: true });

    this.toggleModal = this.toggleModal.bind(this);
    this.onUserAction = this.onUserAction.bind(this);
    this.onSearchWhitelist = this.onSearchWhitelist.bind(this);
  }

  componentDidMount() {
    this.LoggerGroup.emit("Mounted").end();

    this.props.getWhitelist();
  }

  onSearchWhitelist({ target }) {
    this.LoggerGroup.emit("Searching value", target.name, target.value);

    this.setState({ search_text: target.value });
  }

  onUserAction(type, whitelist) {
    this.LoggerGroup.emit("User Action triggered", { dir: whitelist });

    switch (type) {
      case "add":
        this.LoggerGroup.emit(`  [i] Adding Record to whitelist`);
        this.props.selectWhitelist({});
        this.toggleModal(true, this.props.createWhitelist);
        break;

      case "edit":
        this.LoggerGroup.emit(`  [i] Editing Record`);
        this.props.selectWhitelist(whitelist);
        this.toggleModal(true, this.props.updateWhitelist);
        break;

      case "delete":
        this.props.selectWhitelist(whitelist);
        this.LoggerGroup.emit(`  [i] Deleting Record`);
        toastr.confirm("Are you sure you want to remove this user from the whitelist?", {
          okText: "Delete Approver",
          onCancel: () =>
            this.LoggerGroup.emit(`Cancelled delete approver[${whitelist.whitelist_id_bint}]`),
          onOk: () => this.props.deleteWhitelist(whitelist),
        });
        break;

      default:
        return this.LoggerGroup.emit({ error: `Invalid user action type[${type}]` });
    }

    this.LoggerGroup.end();
  }

  toggleModal(show, applyChanges) {
    const { modal } = this.state;
    modal.isOpen = typeof show === "boolean" ? show : !modal.isOpen;
    modal.applyChanges = applyChanges;

    this.setState({ modal });
  }

  render() {
    const { loading, whitelist, selected, internal_user } = this.props;
    const { onUserAction } = this;
    const { search_text, modal, recordsPerPage } = this.state;

    if (loading) {
      return <Loader />;
    }

    //const search_text_regex = new RegExp(search_text, "i")
    const search_text_regex = new RegExp(escapeRegExp(search_text), "i");
    const filtered_whitelist = whitelist.filter(
      (whitelist_record) =>
        search_text_regex.test(whitelist_record.name_vch) ||
        search_text_regex.test(whitelist_record.org_vch) ||
        search_text_regex.test(whitelist_record.device_vch)
    );

    const AddWhitelistRecord = (props) => (
      <Row className={classNames("mb-4", props.className)}>
        <Col className="text-right">
          <Button color="primary" onClick={() => onUserAction("add")}>
            <FontAwesomeIcon icon={faUserPlus} className="mr-2" />
            Add Record to Whitelist
          </Button>
        </Col>
      </Row>
    );

    return (
      <Container fluid className="p-0">
        <Header onSearchWhitelist={this.onSearchWhitelist} />

        {modal.isOpen && (
          <WhitelistModal {...modal} toggleModal={this.toggleModal} selected={selected} />
        )}

        <AddWhitelistRecord />

        <Card>
          <CardBody>
            <p>
              In the QA environment, only records listed in the table below will receive messages
              from campaign messages.
            </p>

            <p>
              <strong>Note:</strong> this will NOT prevent seed or approver messages.
            </p>

            {internal_user ? (
              <Alert color="warning" className="alert-outline">
                <div className="alert-icon">
                  <FontAwesomeIcon icon={faBell} size="lg" className="align-middle" />
                </div>

                <div
                  className="alert-message"
                  style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                >
                  <h4 className="alert-heading">Internal user</h4>
                  Because you are an internal user, whitelist records belonging to the Message
                  Broadcast/MB Organizations are included in the list below.
                  <br />
                  These will <strong>NOT</strong> be shown to external users (like this message)
                </div>
              </Alert>
            ) : null}
          </CardBody>

          <CardBody>
            <BootstrapTable
              bootstrap4
              hover
              className="table-responsive"
              keyField="whitelist_id_bint"
              data={filtered_whitelist}
              columns={whitelist_table_schema({ onUserAction })}
              bordered={false}
              pagination={paginationFactory({
                sizePerPage: Number(recordsPerPage),
                sizePerPageList: [5, 10, 25, 50, 75, 100],
                onSizePerPageChange: (sizePerPage) => {
                  localStorage.setItem(RECORDS_PER_PAGE, sizePerPage);
                },
              })}
              defaultSorted={[
                {
                  dataField: "org_vch",
                  order: "asc",
                },
                {
                  dataField: "name_vch",
                  order: "asc",
                },
              ]}
              rowClasses={(approver) =>
                approver.whitelist_id_bint === selected.whitelist_id_bint ? "table-info" : null
              }
            />
          </CardBody>
        </Card>

        {filtered_whitelist.length > 5 && <AddWhitelistRecord className="d-lg-none" />}
      </Container>
    );
  }
}

Whitelist.propTypes = {
  errors: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  whitelist: PropTypes.array.isRequired,
  selected: PropTypes.object.isRequired,
  internal_user: PropTypes.bool.isRequired,

  // APIs
  getWhitelist: PropTypes.func.isRequired,
  selectWhitelist: PropTypes.func.isRequired,
  createWhitelist: PropTypes.func.isRequired,
  updateWhitelist: PropTypes.func.isRequired,
  deleteWhitelist: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  errors: state.campaigns.errors,
  loading: state.admin.loading.whitelist,
  whitelist: state.admin.whitelist,
  selected: state.admin.selectedWhitelist,
  internal_user: state.admin.whitelistInternal,
});

export default connect(mapStateToProps, {
  getWhitelist,
  createWhitelist,
  selectWhitelist,
  updateWhitelist,
  deleteWhitelist,
})(Whitelist);
